/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useMemo } from "react";
import usePluginUtils from "./usePluginUtils";
import { canCombineService, getCommonProviderIds, isProviderAvailableForService } from "../utils/service";

const useProviderValidation = () => {
  const isAnyProvider = useCallback((provider) => provider?.id === "any", []);
  return { isAnyProvider, isProviderAvailable: isProviderAvailableForService };
};

const useServiceCombination = (selectedServices) => {
  const { isPluginEnabled } = usePluginUtils();
  const isMultiBookingEnabled = isPluginEnabled("multiple_booking");
  const isGroupBookingEnabled = isPluginEnabled("group_booking");

  const canCombine = useCallback(
    (service) => canCombineService(service, { isMultiBookingEnabled, isGroupBookingEnabled }),
    [isMultiBookingEnabled, isGroupBookingEnabled]
  );

  const selectedServicesProviders = useMemo(
    () => [...getCommonProviderIds(selectedServices)],
    [selectedServices]
  );

  const isCombinationAllowed = useCallback(
    (service, services) => {
      if (services.length === 0) return true;

      const isServiceAlreadySelected = services.some((s) => s.id === service.id);
      const hasNonCombinableService = services.some((s) => !canCombine(s));

      if (hasNonCombinableService) return isServiceAlreadySelected;

      return canCombine(service) && services.every(canCombine);
    },
    [canCombine]
  );

  return { selectedServicesProviders, isCombinationAllowed };
};

export const useFilterAllowedServices = (selectedProvider, selectedServices) => {
  const { isPluginEnabled } = usePluginUtils();
  const { isAnyProvider, isProviderAvailable } = useProviderValidation();
  const { selectedServicesProviders, isCombinationAllowed } = useServiceCombination(selectedServices);

  const isProviderValid = useCallback(
    (service, provider) => {
      const isProviderMatch = !provider || isAnyProvider(provider) || isProviderAvailable(service, provider);
      const areProvidersCompatible =
        selectedServicesProviders.length === 0 ||
        service.providers.some((providerId) => selectedServicesProviders.includes(+providerId));

      return isProviderMatch && areProvidersCompatible;
    },
    [isAnyProvider, isProviderAvailable, selectedServicesProviders]
  );

  const checkServiceAvailability = useCallback(
    (service, provider, services) => {
      if (!isPluginEnabled("multiple_booking")) {
        return services.length === 0
          ? provider
            ? isAnyProvider(provider) || 
              (service.providers.includes(provider.id) && service.min_group_booking <= provider.qty)
            : true
          : services.some((s) => s.id === service.id);
      }
      return isProviderValid(service, provider) && isCombinationAllowed(service, services);
    },
    [isProviderValid, isPluginEnabled, isCombinationAllowed, isAnyProvider]
  );

  return useMemo(
    () => (services) =>
      services?.map((service) => ({
        ...service,
        is_available: checkServiceAvailability(service, selectedProvider, selectedServices),
      })),
    [selectedProvider, selectedServices, checkServiceAvailability]
  );
};
